import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../index'
import { ProfileData } from '@/types/profile'

export interface NotificationState {
  publishedNotice: boolean
  emailmagazineForStudent: boolean
  emailmagazineForJobseeker: boolean
  isLoading: boolean
  hasChanges: boolean
  error: string | null
}

interface GenderOption {
  value: number
  name: string
}

interface SettingsState {
  notification: NotificationState
  disabledButtonProfile: boolean
  disabledButtonNotification: boolean
  changePasswordState: boolean
  profile: Partial<ProfileData>
  defaultGender: GenderOption[]
}

const initialState: SettingsState = {
  notification: {
    publishedNotice: false,
    emailmagazineForStudent: false,
    emailmagazineForJobseeker: false,
    isLoading: false,
    hasChanges: false,
    error: null,
  },
  disabledButtonProfile: true,
  disabledButtonNotification: true,
  changePasswordState: false,
  profile: {},
  defaultGender: [
    { value: 1, name: '男性' },
    { value: 2, name: '女性' },
    { value: 3, name: 'その他' },
    { value: 4, name: '回答しない' },
  ],
}

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setNotificationSettings: (
      state,
      action: PayloadAction<{
        publishedNotice: boolean
        emailmagazineForStudent: boolean
        emailmagazineForJobseeker: boolean
      }>,
    ) => {
      const { publishedNotice, emailmagazineForStudent, emailmagazineForJobseeker } = action.payload
      state.notification.publishedNotice = publishedNotice
      state.notification.emailmagazineForStudent = emailmagazineForStudent
      state.notification.emailmagazineForJobseeker = emailmagazineForJobseeker
      state.notification.hasChanges = false
    },
    updatePublishedNotice: (state, action: PayloadAction<boolean>) => {
      state.notification.publishedNotice = action.payload
      state.notification.hasChanges = true
    },
    updateEmailmagazineForStudent: (state, action: PayloadAction<boolean>) => {
      state.notification.emailmagazineForStudent = action.payload
      state.notification.hasChanges = true
    },
    updateEmailmagazineForJobseeker: (state, action: PayloadAction<boolean>) => {
      state.notification.emailmagazineForJobseeker = action.payload
      state.notification.hasChanges = true
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.notification.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.notification.error = action.payload
    },
    resetChanges: (state) => {
      state.notification.hasChanges = false
    },
    setDisabledButtonProfile: (state, action: PayloadAction<boolean>) => {
      state.disabledButtonProfile = action.payload
    },
    setDisabledButtonNotification: (state, action: PayloadAction<boolean>) => {
      state.disabledButtonNotification = action.payload
    },
    gotProfile: (state, action: PayloadAction<ProfileData>) => {
      state.profile = action.payload
    },
    updateProfile: (state, action: PayloadAction<Partial<ProfileData>>) => {
      state.profile = { ...state.profile, ...action.payload }
    },
    updateChangePasswordState: (state, action: PayloadAction<boolean>) => {
      state.changePasswordState = action.payload
    },
  },
})

export const {
  setNotificationSettings,
  updatePublishedNotice,
  updateEmailmagazineForStudent,
  updateEmailmagazineForJobseeker,
  setLoading,
  setError,
  resetChanges,
  setDisabledButtonProfile,
  setDisabledButtonNotification,
  gotProfile,
  updateProfile,
  updateChangePasswordState,
} = settingsSlice.actions

export const selectNotificationSettings = (state: RootState) => state.settings.notification
export const selectHasNotificationChanges = (state: RootState) =>
  state.settings.notification.hasChanges
export const selectIsNotificationLoading = (state: RootState) =>
  state.settings.notification.isLoading
export const selectNotificationError = (state: RootState) => state.settings.notification.error
export const selectProfile = (state: RootState) => state.settings.profile
export const selectDefaultGender = (state: RootState) => state.settings.defaultGender
export const selectDisabledButtonProfile = (state: RootState) =>
  state.settings.disabledButtonProfile

export default settingsSlice.reducer
